import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import WavesurferPlayer from "@wavesurfer/react";
import type WaveSurfer from "wavesurfer.js";
import { LuPause, LuVolume2, LuVolumeX, LuX } from "react-icons/lu";

import { MIN_STREAM_PLAYBACK_BYTES } from "../hooks/useGenerateMusic";
import { PREVIEW_LIMIT_EPSILON_SEC } from "../constants";
import { EQ_FREQUENCIES } from "../utils/equalizerConfig";
import {
  getMusicDisplayFullName,
  getMusicDisplayShortName,
} from "../utils/musicDisplay";

const STREAM_BARS = Array.from({ length: 96 }, (_, i) =>
  Math.round(26 + Math.abs(Math.sin(i * 0.52) * 34 + Math.cos(i * 0.21) * 18)),
);

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const secs = Math.floor(s % 60);
  return `${m}:${secs.toString().padStart(2, "0")}`;
}

function isBlobPlaybackActive(ws: WaveSurfer | null): boolean {
  if (!ws) return false;
  const el = ws.getMediaElement();
  if (el) {
    return !el.paused && !el.ended;
  }
  return ws.isPlaying();
}

interface MusicPlayerProps {
  src?: string;
  liveSrc?: string;
  liveStreamActive?: boolean;
  isGeneratingStream?: boolean;
  streamBytesReceived?: number;
  expectedDurationSeconds?: number;
  onLivePlaybackEnded?: () => void;
  title?: string;
  musicId?: string | null;
  eqValues: number[];
  /** When false, EQ is already baked into the file — bypass the live filter chain. */
  playThroughLiveEq?: boolean;
  equalizerRevision?: number;
  onTimeUpdate?: (seconds: number) => void;
  onDurationChange?: (seconds: number) => void;
  /** When set, playback auto-stops at this time (seconds). Waveform still shows full duration. */
  maxPlaybackSeconds?: number;
  /** Fired once when playback hits maxPlaybackSeconds (e.g. unauth preview limit). */
  onPlaybackLimitReached?: () => void;
  isInstrumental?: boolean;
  isDark?: boolean;
  onDelete?: () => void;
  showLoadingState?: boolean;
}

export interface MusicPlayerHandle {
  pause: () => void;
  resetPreviewLimitState: () => void;
}

const MusicPlayer = forwardRef<MusicPlayerHandle, MusicPlayerProps>(function MusicPlayer({
  src,
  liveSrc,
  liveStreamActive,
  isGeneratingStream,
  streamBytesReceived = 0,
  expectedDurationSeconds,
  onLivePlaybackEnded,
  title,
  musicId,
  eqValues,
  playThroughLiveEq = true,
  equalizerRevision = 0,
  onTimeUpdate,
  onDurationChange,
  maxPlaybackSeconds,
  onPlaybackLimitReached,
  isInstrumental,
  isDark,
  onDelete,
  showLoadingState = false,
}, ref) {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const eqFiltersRef = useRef<BiquadFilterNode[]>([]);
  const gainNodeRef = useRef<GainNode | null>(null);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onDurationChangeRef = useRef(onDurationChange);
  const onPlaybackLimitReachedRef = useRef(onPlaybackLimitReached);
  const maxPlaybackSecondsRef = useRef(maxPlaybackSeconds);
  const liveAudioRef = useRef<HTMLAudioElement>(null);
  const userPausedRef = useRef(false);
  const playbackLimitReachedRef = useRef(false);
  const previewLimitNotifiedRef = useRef(false);
  const isEnforcingLimitRef = useRef(false);
  const userSeekingUntilRef = useRef(0);
  const wiredMediaElementsRef = useRef<WeakSet<HTMLMediaElement>>(new WeakSet());

  const isLivePlayback = !!(liveStreamActive && liveSrc);
  const hasTrack = !!(src || liveSrc);

  useEffect(() => {
    maxPlaybackSecondsRef.current = maxPlaybackSeconds;
  }, [maxPlaybackSeconds]);

  useEffect(() => {
    onPlaybackLimitReachedRef.current = onPlaybackLimitReached;
  }, [onPlaybackLimitReached]);

  const previewListenMsRef = useRef(0);
  const previewWallClockStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    playbackLimitReachedRef.current = false;
    previewLimitNotifiedRef.current = false;
    previewListenMsRef.current = 0;
    previewWallClockStartedAtRef.current = null;
  }, [musicId, src, equalizerRevision, liveSrc]);

  useEffect(() => {
    if (showLoadingState && src && !isLivePlayback) {
      setIsAudioLoading(true);
    } else {
      setIsAudioLoading(false);
    }
  }, [src, equalizerRevision, showLoadingState, isLivePlayback]);

  useImperativeHandle(ref, () => ({
    pause() {
      const liveEl = liveAudioRef.current;
      if (liveEl && !liveEl.paused) {
        userPausedRef.current = true;
        liveEl.pause();
        setIsPlaying(false);
      }
      const ws = wavesurferRef.current;
      if (ws?.isPlaying()) {
        ws.pause();
        setIsPlaying(false);
      }
    },
    resetPreviewLimitState() {
      playbackLimitReachedRef.current = false;
      previewLimitNotifiedRef.current = false;
      previewListenMsRef.current = 0;
      previewWallClockStartedAtRef.current = null;
    },
  }), []);

  const enforcePlaybackLimit = useCallback(
    (time: number, options?: { notify?: boolean }): boolean => {
      const limit = maxPlaybackSecondsRef.current;
      if (!limit || limit <= 0) {
        return false;
      }

      const triggerAt = limit - PREVIEW_LIMIT_EPSILON_SEC;

      if (playbackLimitReachedRef.current) {
        setCurrentTime(limit);
        onTimeUpdateRef.current?.(limit);
        return true;
      }

      if (time < triggerAt) {
        return false;
      }

      playbackLimitReachedRef.current = true;
      previewListenMsRef.current = limit * 1000;
      previewWallClockStartedAtRef.current = null;

      isEnforcingLimitRef.current = true;
      try {
        if (isLivePlayback) {
          const el = liveAudioRef.current;
          if (el) {
            el.pause();
            el.currentTime = limit;
          }
        } else {
          const ws = wavesurferRef.current;
          if (ws) {
            ws.pause();
            ws.setTime(limit);
          }
        }
      } finally {
        isEnforcingLimitRef.current = false;
      }

      setCurrentTime(limit);
      onTimeUpdateRef.current?.(limit);
      setIsPlaying(false);
      if (options?.notify !== false && !previewLimitNotifiedRef.current) {
        previewLimitNotifiedRef.current = true;
        onPlaybackLimitReachedRef.current?.();
      }
      return true;
    },
    [isLivePlayback],
  );

  const applySeekTime = useCallback(
    (time: number, fromUser = false) => {
      const ws = wavesurferRef.current;
      if (!ws || isLivePlayback) {
        return;
      }

      const limit = maxPlaybackSecondsRef.current;
      const duration = ws.getDuration();
      const trackEnd =
        Number.isFinite(duration) && duration > 0 ? duration : Number.POSITIVE_INFINITY;
      const requestedPastPreview = !!(
        limit &&
        limit > 0 &&
        time > limit + PREVIEW_LIMIT_EPSILON_SEC
      );

      let playheadTime = Math.min(Math.max(0, time), trackEnd);
      if (limit && limit > 0) {
        playheadTime = Math.min(playheadTime, limit);
      }

      if (fromUser) {
        userSeekingUntilRef.current = performance.now() + 300;
        previewWallClockStartedAtRef.current = Date.now();
        if (!limit || playheadTime < limit - PREVIEW_LIMIT_EPSILON_SEC) {
          playbackLimitReachedRef.current = false;
          previewLimitNotifiedRef.current = false;
          previewListenMsRef.current = Math.min(
            previewListenMsRef.current,
            playheadTime * 1000,
          );
        }
      }

      const hitPreviewLimit = !!(
        limit &&
        limit > 0 &&
        (requestedPastPreview ||
          playheadTime >= limit - PREVIEW_LIMIT_EPSILON_SEC)
      );

      if (hitPreviewLimit) {
        enforcePlaybackLimit(limit, { notify: true });
        return;
      }

      if (Math.abs(ws.getCurrentTime() - playheadTime) > 0.01) {
        isEnforcingLimitRef.current = true;
        try {
          ws.setTime(playheadTime);
        } finally {
          isEnforcingLimitRef.current = false;
        }
      }
      setCurrentTime(playheadTime);
      onTimeUpdateRef.current?.(playheadTime);
    },
    [isLivePlayback, enforcePlaybackLimit],
  );

  const applySeekTimeRef = useRef(applySeekTime);
  useEffect(() => {
    applySeekTimeRef.current = applySeekTime;
  }, [applySeekTime]);

  const firePreviewLimit = useCallback(() => {
    const limit = maxPlaybackSecondsRef.current;
    if (!limit || playbackLimitReachedRef.current) {
      return;
    }
    enforcePlaybackLimit(limit, { notify: true });
  }, [enforcePlaybackLimit]);

  const handlePreviewPlaybackStart = useCallback(() => {
    setIsPlaying(true);
    if (!maxPlaybackSecondsRef.current || playbackLimitReachedRef.current) {
      return;
    }
    if (previewWallClockStartedAtRef.current == null) {
      previewWallClockStartedAtRef.current = Date.now();
    }
  }, []);

  const handlePreviewPlaybackStop = useCallback(() => {
    setIsPlaying(false);
  }, []);

  useEffect(() => {
    if (!maxPlaybackSeconds || maxPlaybackSeconds <= 0) {
      return;
    }

    const limitMs = maxPlaybackSeconds * 1000;
    const intervalId = window.setInterval(() => {
      if (playbackLimitReachedRef.current) {
        return;
      }

      const playing = isLivePlayback
        ? !!(
          liveAudioRef.current &&
          !liveAudioRef.current.paused &&
          !liveAudioRef.current.ended
        )
        : isBlobPlaybackActive(wavesurferRef.current);

      if (playing) {
        previewListenMsRef.current += 100;
        if (previewWallClockStartedAtRef.current == null) {
          previewWallClockStartedAtRef.current = Date.now();
        }
      }

      const wallClockMs =
        previewWallClockStartedAtRef.current != null
          ? Date.now() - previewWallClockStartedAtRef.current
          : 0;
      if (previewListenMsRef.current >= limitMs || wallClockMs >= limitMs) {
        firePreviewLimit();
      }
    }, 100);

    return () => window.clearInterval(intervalId);
  }, [maxPlaybackSeconds, isLivePlayback, firePreviewLimit]);

  const syncPlaybackTime = useCallback(
    (time: number) => {
      if (isEnforcingLimitRef.current) {
        return;
      }
      if (performance.now() < userSeekingUntilRef.current) {
        return;
      }
      if (playbackLimitReachedRef.current) {
        const limit = maxPlaybackSecondsRef.current;
        if (limit) {
          setCurrentTime(limit);
          onTimeUpdateRef.current?.(limit);
        }
        return;
      }
      if (enforcePlaybackLimit(time)) {
        return;
      }
      onTimeUpdateRef.current?.(time);
      setCurrentTime(time);
    },
    [enforcePlaybackLimit],
  );

  const syncPlaybackTimeRef = useRef(syncPlaybackTime);
  useEffect(() => {
    syncPlaybackTimeRef.current = syncPlaybackTime;
  }, [syncPlaybackTime]);

  useEffect(() => {
    if (!isPlaying || !isLivePlayback) {
      return;
    }

    let rafId = 0;
    const tick = () => {
      const el = liveAudioRef.current;
      if (el && !el.paused) {
        syncPlaybackTime(el.currentTime);
      }
      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, [isPlaying, isLivePlayback, syncPlaybackTime]);

  useEffect(() => {
    if (!maxPlaybackSeconds || !isLivePlayback) {
      return;
    }

    const intervalId = window.setInterval(() => {
      const el = liveAudioRef.current;
      if (el && !el.paused) {
        enforcePlaybackLimit(el.currentTime);
      }
    }, 100);

    return () => window.clearInterval(intervalId);
  }, [maxPlaybackSeconds, isLivePlayback, enforcePlaybackLimit]);

  const displayDuration =
    duration > 0 ? duration : expectedDurationSeconds ?? 0;
  const streamProgressPercent =
    isLivePlayback && displayDuration > 0
      ? Math.min((currentTime / displayDuration) * 100, 100)
      : 0;

  const applyOutputVolume = useCallback(
    (level: number, muted: boolean) => {
      if (isLivePlayback) {
        const el = liveAudioRef.current;
        if (el) {
          el.muted = muted;
          el.volume = level;
        }
        return;
      }
      const ctx = audioContextRef.current;
      const gain = gainNodeRef.current;
      if (ctx && gain) {
        gain.gain.setTargetAtTime(muted ? 0 : level, ctx.currentTime, 0.01);
      }
    },
    [isLivePlayback],
  );

  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
    onDurationChangeRef.current = onDurationChange;
  });

  useEffect(() => {
    userPausedRef.current = false;
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsMuted(false);
  }, [liveSrc ? "live" : src]);

  useEffect(() => {
    const el = liveAudioRef.current;
    if (!el || !liveSrc || !isLivePlayback) return;
    if (el.src === liveSrc) return;

    const savedTime = el.currentTime;
    const shouldResume = !el.paused && !userPausedRef.current;
    el.src = liveSrc;

    const resumeAfterLoad = () => {
      if (savedTime > 0 && Number.isFinite(savedTime)) {
        try {
          el.currentTime = savedTime;
        } catch {
          // duration may not be known yet on growing wav blob
        }
      }
      if (shouldResume) {
        void el.play().then(() => handlePreviewPlaybackStart()).catch(() => { });
      }
    };

    if (el.readyState >= HTMLMediaElement.HAVE_METADATA) {
      resumeAfterLoad();
    } else {
      el.addEventListener("loadedmetadata", resumeAfterLoad, { once: true });
    }
  }, [liveSrc, isLivePlayback, handlePreviewPlaybackStart]);

  const tryResumeLivePlayback = useCallback(() => {
    if (!isLivePlayback || userPausedRef.current) return;
    const el = liveAudioRef.current;
    if (!el || !el.paused) return;
    void el.play().then(() => handlePreviewPlaybackStart()).catch(() => { });
  }, [isLivePlayback, handlePreviewPlaybackStart]);

  useEffect(() => {
    if (!isLivePlayback) return;
    if (streamBytesReceived < MIN_STREAM_PLAYBACK_BYTES) return;
    const el = liveAudioRef.current;
    if (!el || userPausedRef.current) return;
    void el.play()
      .then(() => handlePreviewPlaybackStart())
      .catch(() => { });
  }, [isLivePlayback, streamBytesReceived, handlePreviewPlaybackStart]);

  useEffect(() => {
    const audio = liveAudioRef.current;
    if (!audio || !isLivePlayback) return;

    audio.volume = volume;
    audio.muted = isMuted;

    const handleTime = () => {
      syncPlaybackTime(audio.currentTime);
    };
    const handleDuration = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };
    const handlePlay = () => {
      userPausedRef.current = false;
      handlePreviewPlaybackStart();
    };
    const handlePause = () => {
      if (isLivePlayback && !userPausedRef.current) {
        tryResumeLivePlayback();
        return;
      }
      handlePreviewPlaybackStop();
    };
    const handleEnded = () => {
      setIsPlaying(false);
      onLivePlaybackEnded?.();
    };

    audio.addEventListener("timeupdate", handleTime);
    audio.addEventListener("durationchange", handleDuration);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("waiting", tryResumeLivePlayback);
    audio.addEventListener("stalled", tryResumeLivePlayback);
    audio.addEventListener("canplay", tryResumeLivePlayback);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTime);
      audio.removeEventListener("durationchange", handleDuration);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("waiting", tryResumeLivePlayback);
      audio.removeEventListener("stalled", tryResumeLivePlayback);
      audio.removeEventListener("canplay", tryResumeLivePlayback);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [isLivePlayback, volume, isMuted, tryResumeLivePlayback, onLivePlaybackEnded, syncPlaybackTime, handlePreviewPlaybackStart, handlePreviewPlaybackStop]);

  const onReady = useCallback((ws: WaveSurfer) => {
    setIsAudioLoading(false);
    setWavesurfer(ws);
    wavesurferRef.current = ws;
    const trackDuration = ws.getDuration();
    setDuration(trackDuration);
    onDurationChangeRef.current?.(trackDuration);

    const audioEl = ws.getMediaElement();
    const wasPlaying = !!(audioEl && !audioEl.paused && !audioEl.ended);
    if (!wasPlaying) {
      setCurrentTime(0);
      setIsPlaying(false);
    }
    if (!audioEl) return;

    const alreadyWired =
      wiredMediaElementsRef.current.has(audioEl) && !!audioContextRef.current;

    if (!alreadyWired) {
      if (audioContextRef.current) {
        void audioContextRef.current.close();
        eqFiltersRef.current = [];
        gainNodeRef.current = null;
      }

      const ctx = new AudioContext();
      audioContextRef.current = ctx;

      let source: MediaElementAudioSourceNode;
      try {
        source = ctx.createMediaElementSource(audioEl);
        wiredMediaElementsRef.current.add(audioEl);
      } catch {
        return;
      }

      const filters = EQ_FREQUENCIES.map((freq, index) => {
        const f = ctx.createBiquadFilter();
        f.type = "peaking";
        f.frequency.value = freq;
        f.Q.value = 1;
        f.gain.value = playThroughLiveEq ? (eqValues[index] ?? 0) : 0;
        return f;
      });
      eqFiltersRef.current = filters;

      const gain = ctx.createGain();
      gain.gain.value = isMuted ? 0 : volume;
      gainNodeRef.current = gain;

      if (playThroughLiveEq) {
        source.connect(filters[0]);
        for (let i = 0; i < filters.length - 1; i++) {
          filters[i].connect(filters[i + 1]);
        }
        filters[filters.length - 1].connect(gain);
      } else {
        source.connect(gain);
      }
      gain.connect(ctx.destination);
    } else if (audioContextRef.current?.state === "suspended") {
      void audioContextRef.current.resume();
    }
  }, [eqValues, isMuted, playThroughLiveEq, volume]);

  useEffect(() => {
    if (!wavesurfer || isLivePlayback) {
      return;
    }

    const onTime = (time: number) => {
      syncPlaybackTimeRef.current(time);
    };
    const onInteraction = () => {
      window.requestAnimationFrame(() => {
        applySeekTimeRef.current(wavesurferRef.current?.getCurrentTime() ?? 0, true);
      });
    };

    wavesurfer.on("timeupdate", onTime);
    wavesurfer.on("interaction", onInteraction);
    return () => {
      wavesurfer.un("timeupdate", onTime);
      wavesurfer.un("interaction", onInteraction);
    };
  }, [wavesurfer, isLivePlayback]);

  useEffect(() => {
    if (isLivePlayback) return;
    const audioEl = wavesurferRef.current?.getMediaElement();
    if (!audioEl) return;

    const onMediaPlay = () => handlePreviewPlaybackStart();
    const onMediaPause = () => handlePreviewPlaybackStop();
    audioEl.addEventListener("play", onMediaPlay);
    audioEl.addEventListener("pause", onMediaPause);
    return () => {
      audioEl.removeEventListener("play", onMediaPlay);
      audioEl.removeEventListener("pause", onMediaPause);
    };
  }, [wavesurfer, isLivePlayback, handlePreviewPlaybackStart, handlePreviewPlaybackStop]);

  useEffect(() => {
    if (isLivePlayback) return;
    applyOutputVolume(volume, isMuted);
  }, [volume, isMuted, isLivePlayback, applyOutputVolume]);

  useEffect(() => {
    const ctx = audioContextRef.current;
    if (!ctx || !eqFiltersRef.current.length || !playThroughLiveEq) return;
    eqValues.forEach((db, i) => {
      const filter = eqFiltersRef.current[i];
      if (filter) filter.gain.setTargetAtTime(db, ctx.currentTime, 0.01);
    });
  }, [eqValues, playThroughLiveEq]);

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        void audioContextRef.current.close();
      }
    };
  }, []);

  async function handlePlayPause() {
    if (isLivePlayback) {
      const el = liveAudioRef.current;
      if (!el) return;
      if (el.paused) {
        if (
          maxPlaybackSeconds &&
          el.currentTime >= maxPlaybackSeconds
        ) {
          enforcePlaybackLimit(el.currentTime, { notify: true });
          return;
        }
        userPausedRef.current = false;
        await el.play();
      } else {
        userPausedRef.current = true;
        el.pause();
        setIsPlaying(false);
      }
      return;
    }
    if (!wavesurfer) return;
    if (audioContextRef.current?.state === "suspended") {
      await audioContextRef.current.resume();
    }
    if (
      maxPlaybackSeconds &&
      !wavesurfer.isPlaying() &&
      wavesurfer.getCurrentTime() >= maxPlaybackSeconds
    ) {
      enforcePlaybackLimit(wavesurfer.getCurrentTime(), { notify: true });
      return;
    }
    const wasPlaying = wavesurfer.isPlaying();
    void wavesurfer.playPause();
    if (!wasPlaying) {
      handlePreviewPlaybackStart();
    }
  }

  function handleSkip(seconds: number) {
    if (isLivePlayback) return;
    const ws = wavesurferRef.current;
    if (!ws) return;

    const limit = maxPlaybackSecondsRef.current;
    const targetTime = ws.getCurrentTime() + seconds;
    if (
      limit &&
      seconds > 0 &&
      targetTime >= limit - PREVIEW_LIMIT_EPSILON_SEC
    ) {
      applySeekTime(Math.max(targetTime, limit), true);
      return;
    }

    applySeekTime(targetTime, true);
  }

  function handleVolumeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = parseFloat(e.target.value);
    setVolume(v);
    const muted = v === 0;
    setIsMuted(muted);
    applyOutputVolume(v, muted);
  }

  function toggleMute() {
    setIsMuted((prev) => {
      const nextMuted = !prev;
      applyOutputVolume(volume, nextMuted);
      return nextMuted;
    });
  }

  const effectiveVolume = isMuted ? 0 : volume;

  const displayTitle =
    getMusicDisplayShortName(musicId, title) ?? t("musicPlayer.customTrack");
  const displayTitleFull = getMusicDisplayFullName(musicId, title);

  const subtitle = isGeneratingStream
    ? t("musicPlayer.streaming", "Streaming…")
    : isInstrumental
      ? t("musicPlayer.instrumental")
      : t("musicPlayer.vocals");

  if (!hasTrack) {
    return (
      <div className="bg-white dark:bg-[#16283d] border-2 border-[#eaebed] dark:border-[#264a73] rounded-[10px] flex flex-col gap-2.5 items-center justify-center p-3.75 w-full">
        <div className="flex gap-2.5 items-start justify-center w-full">
          <div className="flex flex-[1_0_0] gap-2.5 items-center min-w-0">
            <div className="bg-[#e7f1ff] dark:bg-[#264a73] rounded-md size-10 flex items-center justify-center shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="21"
                height="26"
                viewBox="0 0 21 26"
                fill="none"
              >
                <path
                  d="M7.17825 9.04299L19.9995 5.37979M19.9995 13.3814V17.9604C19.9993 18.5574 19.8048 19.1381 19.4452 19.6146C19.0857 20.0912 18.5807 20.4376 18.0067 20.6016L16.3949 21.062C16.1154 21.1464 15.8219 21.1744 15.5315 21.1444C15.2411 21.1144 14.9596 21.0269 14.7033 20.887C14.4471 20.7471 14.2212 20.5577 14.0388 20.3297C13.8564 20.1017 13.7212 19.8397 13.6411 19.559C13.5609 19.2783 13.5374 18.9844 13.5719 18.6945C13.6063 18.4046 13.6981 18.1244 13.8419 17.8703C13.9857 17.6162 14.1786 17.3933 14.4093 17.2144C14.6401 17.0355 14.9041 16.9044 15.186 16.8285L18.0067 16.0226C18.5807 15.8586 19.0857 15.5122 19.4452 15.0356C19.8048 14.5591 19.9993 13.9784 19.9995 13.3814ZM19.9995 13.3814V0.800781L7.17825 4.46398V17.0446M7.17825 17.0446V21.6236C7.17813 22.2206 6.98357 22.8013 6.62402 23.2778C6.26446 23.7544 5.75948 24.1009 5.18547 24.2648L3.57366 24.7252C3.01545 24.8754 2.42057 24.8 1.91746 24.5153C1.41435 24.2306 1.04339 23.7595 0.884669 23.2037C0.725949 22.6478 0.792208 22.0519 1.06914 21.5445C1.34607 21.0371 1.81146 20.6589 2.3648 20.4917L5.18547 19.6858C5.75948 19.5218 6.26446 19.1754 6.62402 18.6988C6.98357 18.2223 7.17813 17.6416 7.17825 17.0446Z"
                  stroke={isDark ? "#FDFDFD" : "#0E76FF"}
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="flex flex-col items-start leading-normal">
              <p className="font-semibold text-[12px] text-[#16283d] dark:text-[#FDFDFD] sm:block hidden">
                {t("musicPlayer.customTrack")}
              </p>
              <p className="font-semibold text-[12px] text-[#16283d] dark:text-[#FDFDFD] sm:hidden block">
                --
              </p>
              <p className={`font-normal text-[12px] ${isDark ? "text-[#DBDEE1]" : "text-[#687585]"}`}>
                {isInstrumental
                  ? t("musicPlayer.instrumental")
                  : t("musicPlayer.vocals")}
              </p>
            </div>
          </div>
          <div className="flex gap-2 items-center shrink-0">
            <button
              type="button"
              // onClick={toggleMute}
              className="shrink-0 text-[#264a73] hover:text-[#0e76ff] transition-colors cursor-not-allowed"
              aria-label={
                isMuted ? t("musicPlayer.unmute") : t("musicPlayer.mute")
              }
            >
              {isMuted || volume === 0 ? (
                <LuVolumeX className={`size-[19px] cursor-not-allowed ${isDark ? "text-[#fff]" : "text-[#EAEBED]"}`} />
              ) : (
                <LuVolume2 className={`size-[19px] cursor-not-allowed ${isDark ? "text-[#fff]" : "text-[#EAEBED]"}`} />
              )}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={0.5}
              // onChange={handleVolumeChange}
              className={`volume-slider ${isDark ? "disabled-volume-slider-dark" : "disabled-volume-slider"}   flex-[1_0_0]`}
              style={{
                background: `linear-gradient(to right, ${isDark ? "#FFFFFF" : "#EAEBED"} ${effectiveVolume * 50}%, ${isDark ? "#264a73" : "#f6f6f6"} ${effectiveVolume * 50}%)`,
              }}
            />
            {/* <button type="button" className="shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14.6668 1.33203L1.33423 14.6654M14.6668 14.6654L1.3335 1.33203" stroke="#E4E4E4" strokeWidth="2.66667" strokeLinecap="round" />
              </svg>
            </button> */}
          </div>
        </div>
        <div className="bg-[#f7f7f7] dark:bg-[#1c344f] sm:h-37.5 h-[calc(100dvh-445px)] flex items-center justify-center overflow-clip pl-5 pr-2.5 py-5 rounded-[10.728px] w-full">
          <p className="text-[16px] text-[#a1a7b0] font-normal text-center leading-normal sm:max-w-[100%] max-w-[210px]">
            {t("musicPlayer.generatePrompt")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white dark:bg-[#16283d] border-2 border-[#eaebed] dark:border-[#264a73] rounded-[10px] flex flex-col gap-2.5 p-3.75 w-full">
      {isLivePlayback && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={liveAudioRef}
          playsInline
          preload="auto"
          className="hidden"
        />
      )}

      <div className="flex gap-2.5 items-start w-full">
        <div className="flex flex-[1_0_0] gap-2.5 items-center min-w-0">
          <div className="bg-[#e7f1ff] dark:bg-[#E7F1FF] rounded-md size-10 flex items-center justify-center shrink-0">
            {isDark ? <svg xmlns="http://www.w3.org/2000/svg" width="21" height="26" viewBox="0 0 21 26" fill="none">
              <path d="M7.17821 9.04299L19.9994 5.37979M19.9994 13.3814V17.9604C19.9993 18.5574 19.8047 19.1381 19.4452 19.6146C19.0856 20.0912 18.5806 20.4376 18.0066 20.6016L16.3948 21.062C16.1154 21.1464 15.8219 21.1744 15.5315 21.1444C15.2411 21.1144 14.9595 21.0269 14.7033 20.887C14.447 20.7471 14.2211 20.5577 14.0388 20.3297C13.8564 20.1017 13.7212 19.8397 13.641 19.559C13.5609 19.2783 13.5373 18.9844 13.5718 18.6945C13.6063 18.4046 13.6981 18.1244 13.8419 17.8703C13.9856 17.6162 14.1785 17.3933 14.4093 17.2144C14.64 17.0355 14.904 16.9044 15.186 16.8285L18.0066 16.0226C18.5806 15.8586 19.0856 15.5122 19.4452 15.0356C19.8047 14.5591 19.9993 13.9784 19.9994 13.3814ZM19.9994 13.3814V0.800781L7.17821 4.46398V17.0446M7.17821 17.0446V21.6236C7.17808 22.2206 6.98353 22.8013 6.62397 23.2778C6.26442 23.7544 5.75943 24.1009 5.18542 24.2648L3.57361 24.7252C3.01541 24.8754 2.42052 24.8 1.91742 24.5153C1.41431 24.2306 1.04334 23.7595 0.884623 23.2037C0.725903 22.6478 0.792162 22.0519 1.06909 21.5445C1.34603 21.0371 1.81141 20.6589 2.36476 20.4917L5.18542 19.6858C5.75943 19.5218 6.26442 19.1754 6.62397 18.6988C6.98353 18.2223 7.17808 17.6416 7.17821 17.0446Z" stroke="#0E76FF" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            </svg> : <svg
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="26"
              viewBox="0 0 21 26"
              fill="none"
            >
              <path
                d="M7.17825 9.04299L19.9995 5.37979M19.9995 13.3814V17.9604C19.9993 18.5574 19.8048 19.1381 19.4452 19.6146C19.0857 20.0912 18.5807 20.4376 18.0067 20.6016L16.3949 21.062C16.1154 21.1464 15.8219 21.1744 15.5315 21.1444C15.2411 21.1144 14.9596 21.0269 14.7033 20.887C14.4471 20.7471 14.2212 20.5577 14.0388 20.3297C13.8564 20.1017 13.7212 19.8397 13.6411 19.559C13.5609 19.2783 13.5374 18.9844 13.5719 18.6945C13.6063 18.4046 13.6981 18.1244 13.8419 17.8703C13.9857 17.6162 14.1786 17.3933 14.4093 17.2144C14.6401 17.0355 14.9041 16.9044 15.186 16.8285L18.0067 16.0226C18.5807 15.8586 19.0857 15.5122 19.4452 15.0356C19.8048 14.5591 19.9993 13.9784 19.9995 13.3814ZM19.9995 13.3814V0.800781L7.17825 4.46398V17.0446M7.17825 17.0446V21.6236C7.17813 22.2206 6.98357 22.8013 6.62402 23.2778C6.26446 23.7544 5.75948 24.1009 5.18547 24.2648L3.57366 24.7252C3.01545 24.8754 2.42057 24.8 1.91746 24.5153C1.41435 24.2306 1.04339 23.7595 0.884669 23.2037C0.725949 22.6478 0.792208 22.0519 1.06914 21.5445C1.34607 21.0371 1.81146 20.6589 2.3648 20.4917L5.18547 19.6858C5.75948 19.5218 6.26446 19.1754 6.62402 18.6988C6.98357 18.2223 7.17813 17.6416 7.17825 17.0446Z"
                stroke="#0E76FF"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>}

          </div>
          <div className="flex flex-col items-start leading-normal min-w-0">
            <p
              className="font-semibold sm:font-normal text-[12px] sm:text-[16px] text-[#687585] dark:text-[#FDFDFD] truncate w-full"
              title={displayTitleFull}
            >
              {displayTitle}
            </p>
            <p
              className={`font-normal text-[12px] sm:text-[14px] sm:block hidden ${isGeneratingStream
                ? "text-[#0e76ff] animate-pulse"
                : `${isDark ? "text-[#DBDEE1]" : "text-[#687585]"}`
                }`}
            >
              {subtitle}
            </p>
            {/* mobile */}
            <p
              className={`font-normal text-[12px] sm:text-[14px] sm:hidden block ${isGeneratingStream
                ? "text-[#0e76ff] animate-pulse"
                : `${isDark ? "text-[#DBDEE1]" : "text-[#687585]"}`
                }`}
            >
              Vocals
            </p>
            {/* mobile */}
          </div>
        </div>

        <div className="gap-2 items-center shrink-0 hidden sm:flex sm:pr-5">
          <button
            type="button"
            id="muteButton"
            onClick={toggleMute}
            className={`shrink-0  ${isDark ? "text-[#fff]" : "text-[#264a73]"} hover:text-[#0e76ff] transition-colors`}
            aria-label={
              isMuted ? t("musicPlayer.unmute") : t("musicPlayer.mute")
            }
          >
            {isMuted || volume === 0 ? (
              <LuVolumeX className="size-[19px]" />
            ) : (
              <LuVolume2 className="size-[19px]" />
            )}
          </button>
          <input
            type="range"
            id="volumeSlider"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={handleVolumeChange}
            className={`sm:w-[100px] w-[100px] volume-slider flex-[1_0_0] cursor-pointer ${isDark ? "disabled-volume-slider-dark cursor-pointer" : ""}`}
            style={{
              background: `linear-gradient(to right,${isDark ? "#FFFFFF" : "#687585"} ${effectiveVolume * 100}%, ${isDark ? "#264a73" : "#eaebed"}  ${effectiveVolume * 100}%)`,
            }}
          />
          {onDelete && (
            <button type="button" id="cancel-btn" onClick={() => setIsConfirmOpen(true)} className="shrink-0 pl-[14px]">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M14.6668 1.33203L1.33423 14.6654M14.6668 14.6654L1.3335 1.33203" stroke="#E4E4E4" strokeWidth="2.66667" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="w-full rounded-[10.728px] overflow-hidden p-1.5">
        <div className="relative min-h-40">
          {showLoadingState && isAudioLoading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-[#f7f7f7] dark:bg-[#1c344f] rounded-[10.728px]">
              <div className="flex items-center justify-center gap-1.5">
                <span className="size-2 rounded-full bg-[#0e76ff] animate-bounce [animation-delay:0ms]" />
                <span className="size-2 rounded-full bg-[#0e76ff] animate-bounce [animation-delay:150ms]" />
                <span className="size-2 rounded-full bg-[#0e76ff] animate-bounce [animation-delay:300ms]" />
              </div>
              <span className="text-[13px] font-semibold text-[#687585] dark:text-[#DBDEE1]">
                {t("app.loadingSong") || "Loading song..."}
              </span>
            </div>
          )}
          {src && !isLivePlayback && (
            <WavesurferPlayer
              key={`${src}:${equalizerRevision}:${playThroughLiveEq ? "live" : "baked"}`}
              height={120}
              barWidth={3}
              barGap={2}
              barRadius={999}
              cursorWidth={4}
              waveColor={isDark ? "#264a73" : "#dbdee1"}
              progressColor={isDark ? "#FDFDFD" : "#ff944d"}
              cursorColor={isDark ? "#FE6D07" : "#0e76ff"}
              url={src}
              normalize={false}
              onReady={onReady}
              onPlay={() => handlePreviewPlaybackStart()}
              onPause={() => handlePreviewPlaybackStop()}
            />
          )}
          {isLivePlayback && (
            <div className="absolute inset-0 flex items-center justify-center gap-0.5 px-1 overflow-hidden">
              {STREAM_BARS.map((h, i) => {
                const barProgress = ((i + 0.5) / STREAM_BARS.length) * 100;
                const played = barProgress <= streamProgressPercent;
                return (
                  <div
                    key={i}
                    className="flex-1 rounded-[2px] mr-[2px] pr-[4px]"
                    style={{
                      height: `${h + 10}%`,
                      backgroundColor: played ? "#ff944d" : isDark ? "#264a73" : "#dbdee1",
                    }}
                  />
                );
              })}
              {1 > 0 && (
                <div
                  className="absolute top-0 bottom-0 w-[4px] bg-[#0e76ff] rounded-[2px]"
                  style={{ left: `calc(20% - 0px)` }}
                />
              )}
            </div>
          )}
        </div>
      
      </div>
  <div className="static sm:absolute bottom-[20px] left-0 w-full px-0 sm:px-[20px] flex items-center justify-between text-[14px] text-[#264a73] dark:text-[#FDFDFD]">
          <span>{formatTime(currentTime)}</span>
          <span>
            {isLivePlayback && duration === 0 && !expectedDurationSeconds
              ? "—"
              : displayDuration > 0
                ? formatTime(displayDuration)
                : "—"}
          </span>
        </div>
      <div className="flex items-center justify-center gap-10 relative z-10">
        <button
          type="button"
          id="skip-back"
          onClick={() => handleSkip(-10)}
          disabled={isLivePlayback || (showLoadingState && isAudioLoading)}
          className="w-[25] h-[25] relative text-[#687585] hover:text-[#16283d] dark:hover:text-[#fdfdfd] transition-colors disabled:opacity-40"
          aria-label={t("musicPlayer.skipBack")}
        >
          <svg
            width="33"
            height="33"
            viewBox="0 0 33 33"
            fill="none"
            className="w-full"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M1.15387 16.1543C1.15387 19.121 2.0336 22.0211 3.68183 24.4878C5.33005 26.9546 7.67272 28.8772 10.4136 30.0125C13.1545 31.1478 16.1705 31.4448 19.0802 30.8661C21.9899 30.2873 24.6627 28.8587 26.7605 26.7609C28.8582 24.6631 30.2869 21.9904 30.8656 19.0806C31.4444 16.1709 31.1474 13.1549 30.0121 10.414C28.8767 7.67315 26.9542 5.33047 24.4874 3.68225C22.0207 2.03403 19.1206 1.1543 16.1539 1.1543C11.9605 1.17007 7.93548 2.80634 4.92054 5.72096L1.15387 9.48763" stroke={isDark ? "#FDFDFD" : "#687585"} strokeWidth="2.30769" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M1.73079 1.1543L1.15387 9.48763L9.23079 8.6543" stroke={isDark ? "#FDFDFD" : "#687585"} strokeWidth="2.30769" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M11.922 20.7693V12.6924H9.22974" stroke={isDark ? "#FDFDFD" : "#687585"} strokeWidth="2.30769" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9.2298 20.7695H14.6144" stroke={isDark ? "#FDFDFD" : "#687585"} strokeWidth="2.30769" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M22.691 15.3847C22.691 13.8978 21.4856 12.6924 19.9987 12.6924C18.5118 12.6924 17.3064 13.8978 17.3064 15.3847V18.077C17.3064 19.5639 18.5118 20.7693 19.9987 20.7693C21.4856 20.7693 22.691 19.5639 22.691 18.077V15.3847Z" stroke={isDark ? "#FDFDFD" : "#687585"} strokeWidth="2.30769" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          type="button"
          id="play-pause"
          onClick={handlePlayPause}
          disabled={isLivePlayback ? false : (showLoadingState && isAudioLoading)}
          className={`size-[45px] rounded-full  flex items-center justify-center text-white hover:bg-[#4d5866] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDark ?"bg-[#0E76FF]" : "bg-[#687585]"}`}
          aria-label={
            isPlaying ? t("musicPlayer.pause") : t("musicPlayer.play")
          }
        >
          {isPlaying ? (
            <LuPause className="size-5" /> 
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="19"
              height="19"
              viewBox="0 0 10 19"
              fill="none"
            >
              <path
                d="M14.0643 10.2932L1.52034 18.452C0.846631 18.8931 0 18.3447 0 17.4627V1.14493C0 0.262888 0.846631 -0.285356 1.52034 0.155665L14.0643 8.31452C14.7381 8.75554 14.7381 9.85217 14.0643 10.2932Z"
                fill="white"
              />
            </svg>
          )}
        </button>
        <button
          type="button"
          id="skip-forward"
          onClick={() => handleSkip(10)}
          disabled={isLivePlayback}
          className="w-[25] h-[25] relative text-[#687585] hover:text-[#16283d] dark:hover:text-[#fdfdfd] transition-colors disabled:opacity-40"
          aria-label={t("musicPlayer.skipForward")}
        >
          <svg
            width="33"
            height="33"
            viewBox="0 0 33 33"
            fill="none"
            className="w-full"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M31.1539 16.1543C31.1539 19.121 30.2741 22.0211 28.6259 24.4878C26.9777 26.9546 24.635 28.8772 21.8941 30.0125C19.1532 31.1478 16.1372 31.4448 13.2275 30.8661C10.3178 30.2873 7.64506 28.8587 5.54728 26.7609C3.44949 24.6631 2.02088 21.9904 1.4421 19.0806C0.86332 16.1709 1.16037 13.1549 2.29568 10.414C3.431 7.67315 5.35359 5.33047 7.82032 3.68225C10.2871 2.03403 13.1872 1.1543 16.1539 1.1543C20.3473 1.17007 24.3723 2.80634 27.3872 5.72096L31.1539 9.48763" stroke={isDark ? "#FDFDFD" : "#687585"} strokeWidth="2.30769" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M30.5769 1.1543L31.1539 9.48763L23.0769 8.6543" stroke={isDark ? "#FDFDFD" : "#687585"} strokeWidth="2.30769" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12.3078 20.7693V12.6924H9.61548" stroke={isDark ? "#FDFDFD" : "#687585"} strokeWidth="2.30769" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9.61566 20.7695H15.0003" stroke={isDark ? "#FDFDFD" : "#687585"} strokeWidth="2.30769" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M23.0769 15.3847C23.0769 13.8978 21.8715 12.6924 20.3846 12.6924C18.8976 12.6924 17.6923 13.8978 17.6923 15.3847V18.077C17.6923 19.5639 18.8976 20.7693 20.3846 20.7693C21.8715 20.7693 23.0769 19.5639 23.0769 18.077V15.3847Z" stroke={isDark ? "#FDFDFD" : "#687585"} strokeWidth="2.30769" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {isConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 sm:px-0 px-4"
          onClick={() => setIsConfirmOpen(false)}
        >
          <div
            className="bg-white dark:bg-[#16283d] border-2 border-[#d2e5ff] dark:border-[#264a73] rounded-[10px] w-[400px] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative flex items-center p-[20px] pb-[10px]  dark:border-[#264a73]">
              <div className="flex flex-col justify-center items-center m-auto">
                <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" fill="none">
                  <rect width="50" height="50" rx="8.33333" fill="#FFD6D6" />
                  <path d="M24.9998 19.4221V22.6878M24.9998 29.2193H25.0162M13.6871 35.7507H36.3126C38.8265 35.7507 40.3977 33.0292 39.1408 30.8521L27.828 11.2579C26.5711 9.08072 23.4286 9.08071 22.1716 11.2579L10.8589 30.8521C9.60193 33.0292 11.1731 35.7507 13.6871 35.7507Z" stroke="#DA0000" stroke-width="2.09005" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <h2 className="mt-[15px] text-center font-bold leading-[120%] tracking-[0.32px] text-[20px]  text-[#16283d] dark:text-[#fdfdfd]">
                  Are you sure you want to <br className="sm:block hidden" /> delete <br className="sm:hidden block" /> your music?
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setIsConfirmOpen(false)}
                className="absolute right-0 top-0 flex size-[50px] items-center justify-center text-[#a1a7b0] hover:text-[#16283d] dark:hover:text-[#fdfdfd] transition-colors"
              >
                <LuX className="size-5" />
              </button>
            </div>
            <div className="flex flex-col gap-[30px] p-[20px] pt-[0px]">

              <p className="text-center font-['Rethink_Sans'] text-base not-italic font-normal leading-[120%] tracking-[0.32px] text-[16px] text-[#687585] dark:text-[#c5cdd6]">
                This action is irreversible—<br className="sm:hidden block" />deleting your <br className="sm:block hidden" />  generation will permanently remove it
              </p>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setIsConfirmOpen(false)}
                  className="rounded-full bg-[#eaebed] dark:bg-[#687585] px-[40px] py-[15px] text-[18px] font-bold tracking-[0.36px] text-[#a1a7b0] dark:text-[#fdfdfd] transition-colors hover:bg-[#d1d5d9] dark:hover:bg-[#687585]"
                >
                  CANCEL
                </button>
                <button
                  type="button"
                  onClick={() => {
                    wavesurfer?.pause();
                    liveAudioRef.current?.pause();
                    onDelete?.();
                    setIsConfirmOpen(false);
                  }}
                  className="rounded-full px-[40px] py-[15px] text-[18px] font-bold tracking-[0.36px] text-white transition-opacity hover:opacity-90"
                  style={{
                    background:
                      "radial-gradient(ellipse at 0% 0%, #f95a38 0%, #fc792e 50%, #ff9823 100%)",
                  }}
                >
                  DELETE
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default MusicPlayer;
